import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/store/StoreContext";
import { api, imgSrc } from "@/lib/api";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const FIELDS = [
  { key: "name", label: "Full Name", type: "text", placeholder: "Your full name" },
  { key: "email", label: "Email Address", type: "email", placeholder: "you@example.com" },
  { key: "phone", label: "Phone Number (with country code)", type: "tel", placeholder: "+91 98765 43210" },
  { key: "profession", label: "Profession", type: "text", placeholder: "Your profession" },
];

export default function Checkout() {
  const { cart, totals, currency, clearCart, config, priceOf, money } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", profession: "", message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [demoOrder, setDemoOrder] = useState(null);

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address";
    if (!/^\+?[\d\s-]{8,18}$/.test(form.phone.trim())) e.phone = "Enter a valid phone with country code";
    if (form.address.trim().length < 10) e.address = "Enter your complete delivery address";
    if (form.profession.trim().length < 2) e.profession = "Please enter your profession";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const verifyAndFinish = async (orderId, payload) => {
    await api.post(`/orders/${orderId}/verify`, payload);
    const { data: order } = await api.get(`/orders/${orderId}`);
    sessionStorage.setItem("rl_last_order", JSON.stringify(order));
    clearCart();
    navigate("/thankyou");
  };

  const pay = async () => {
    if (!validate()) { toast.error("Please fix the highlighted fields"); return; }
    if (!cart.length) { toast.error("Your cart is empty"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        customer: form,
        items: cart.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        currency,
      });

      if (data.mode === "demo") {
        setDemoOrder(data);
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: Math.round(data.amount * 100),
        currency: data.currency,
        name: "Rudralife",
        description: `Order ${data.order_id}`,
        order_id: data.razorpay_order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#B08D2B" },
        handler: (resp) => verifyAndFinish(data.order_id, {
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        }).catch(() => toast.error("Payment verification failed — contact support")),
        modal: { ondismiss: () => { api.post(`/orders/${data.order_id}/fail`).catch(() => {}); toast("Payment cancelled"); } },
      });
      rzp.on("payment.failed", () => { api.post(`/orders/${data.order_id}/fail`).catch(() => {}); toast.error("Payment failed"); });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create order, please try again");
    } finally {
      setBusy(false);
    }
  };

  const demoPay = async (success) => {
    if (!demoOrder) return;
    setBusy(true);
    try {
      if (success) await verifyAndFinish(demoOrder.order_id, { demo: true });
      else { await api.post(`/orders/${demoOrder.order_id}/fail`); toast.error("Demo payment failed"); setDemoOrder(null); }
    } finally { setBusy(false); }
  };

  const inputBase = "w-full rounded-md border bg-white px-4 py-3 text-sm text-[#2B1E12] outline-none transition-colors placeholder:text-[#6B5B45]/50 focus:border-[#B08D2B]";

  return (
    <div className="min-h-screen bg-[#FDF8F0] text-[#2B1E12]" data-testid="checkout-page">
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <Link to="/" data-testid="checkout-back" className="mb-8 inline-flex items-center gap-2 text-sm text-[#6B5B45] transition-colors hover:text-[#8F6E1D]">
          <ArrowLeft className="h-4 w-4" /> Back to Collection
        </Link>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-5xl">Secure <span className="text-gold-gradient italic">Checkout</span></h1>

        {cart.length === 0 ? (
          <div data-testid="checkout-empty" className="mt-16 text-center text-[#6B5B45]">
            <p className="font-display text-xl italic">Your cart is empty.</p>
            <Link to="/" className="mt-6 inline-block bg-[#D4AF37] px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#1C130B]">Browse Collection</Link>
          </div>
        ) : (
          <div className="mt-10 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div className="rounded-md border border-[#E9DFC8] bg-white p-7">
              <h2 className="mb-6 text-[11px] font-bold uppercase tracking-[0.26em] text-[#8F6E1D]">Your Details</h2>
              <div className="grid gap-5 sm:grid-cols-2">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="mb-1.5 block text-xs uppercase tracking-wider text-[#6B5B45]">{f.label} *</label>
                    <input
                      data-testid={`checkout-${f.key}`}
                      type={f.type}
                      value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className={`${inputBase} ${errors[f.key] ? "border-red-500" : "border-[#E0D3B4]"}`}
                    />
                    {errors[f.key] && <p data-testid={`error-${f.key}`} className="mt-1 flex items-center gap-1 text-xs text-red-500"><AlertCircle className="h-3 w-3" />{errors[f.key]}</p>}
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs uppercase tracking-wider text-[#6B5B45]">Complete Delivery Address *</label>
                  <textarea
                    data-testid="checkout-address"
                    rows={3}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="House / street / city / state / PIN"
                    className={`${inputBase} ${errors.address ? "border-red-500" : "border-[#E0D3B4]"}`}
                  />
                  {errors.address && <p data-testid="error-address" className="mt-1 flex items-center gap-1 text-xs text-red-500"><AlertCircle className="h-3 w-3" />{errors.address}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs uppercase tracking-wider text-[#6B5B45]">Your Message (optional)</label>
                  <textarea
                    data-testid="checkout-message"
                    rows={2}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Any note for us"
                    className={`${inputBase} border-[#E0D3B4]`}
                  />
                </div>
              </div>
            </div>

            <div className="h-fit rounded-md border border-[#E9DFC8] bg-white p-7 lg:sticky lg:top-8">
              <h2 className="mb-6 text-[11px] font-bold uppercase tracking-[0.26em] text-[#8F6E1D]">Order Summary</h2>
              <ul className="mb-6 space-y-4">
                {cart.map(({ product, quantity }) => (
                  <li key={product.id} className="flex items-center gap-3">
                    <img src={imgSrc(product.image)} alt={product.name} className="h-14 w-14 rounded-md bg-[#F7F0DF] object-contain p-1" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{product.name}</p>
                      <p className="text-xs text-[#6B5B45]">Qty {quantity}</p>
                    </div>
                    <span className="font-display text-base font-bold text-[#8F6E1D]">{money(priceOf(product) * quantity)}</span>
                  </li>
                ))}
              </ul>
              <dl className="space-y-2 border-t border-[#E9DFC8] pt-4 text-sm">
                <div className="flex justify-between text-[#6B5B45]"><dt>Subtotal</dt><dd data-testid="summary-subtotal">{money(totals.subtotal)}</dd></div>
                <div className="flex justify-between text-[#6B5B45]"><dt>Shipping {currency === "USD" ? "(international)" : "(India)"}</dt><dd data-testid="summary-shipping" className={totals.shipping === 0 ? "font-semibold text-green-700" : ""}>{totals.shipping === 0 ? "FREE" : money(totals.shipping)}</dd></div>
                <div className="flex justify-between border-t border-[#E9DFC8] pt-3 text-lg font-bold">
                  <dt>Total</dt><dd data-testid="summary-total" className="font-display text-2xl text-[#8F6E1D]">{money(totals.total)}</dd></div>
              </dl>
              <button
                data-testid="pay-now"
                onClick={pay}
                disabled={busy}
                className="mt-6 flex w-full items-center justify-center gap-2 bg-[#D4AF37] py-4 text-xs font-bold uppercase tracking-[0.22em] text-[#1C130B] transition-colors duration-300 hover:bg-[#8F6E1D] hover:text-white disabled:opacity-60"
              >
                <Lock className="h-4 w-4" /> {busy ? "Processing…" : `Pay Now · ${money(totals.total)}`}
              </button>
              <p className="mt-3 text-center text-[11px] text-[#6B5B45]">
                {config.payments_mode === "live" ? "Secured by Razorpay · Cards, UPI, NetBanking" : "Demo payment mode — Razorpay keys not configured"}
              </p>
            </div>
          </div>
        )}
      </div>

      {demoOrder && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#1C130B]/70 p-5 backdrop-blur-sm" data-testid="demo-payment-modal">
          <div className="w-full max-w-sm rounded-md border border-[#E9DFC8] bg-white p-8 text-center">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.28em] text-[#B34A00]">Demo Mode — No Real Charge</p>
            <h3 className="font-display text-2xl font-semibold text-[#2B1E12]">Razorpay Checkout (Simulated)</h3>
            <p className="mt-3 text-sm text-[#6B5B45]">Order <span className="font-semibold text-[#8F6E1D]">{demoOrder.order_id}</span><br />Amount: <span className="font-display text-lg font-bold text-[#8F6E1D]">{demoOrder.currency === "USD" ? "$" : "₹"}{demoOrder.amount.toLocaleString()}</span></p>
            <div className="mt-6 flex flex-col gap-3">
              <button data-testid="demo-pay-success" onClick={() => demoPay(true)} disabled={busy}
                className="w-full bg-[#D4AF37] py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-[#1C130B] transition-colors hover:bg-[#8F6E1D] hover:text-white">
                Simulate Successful Payment
              </button>
              <button data-testid="demo-pay-fail" onClick={() => demoPay(false)} disabled={busy}
                className="w-full border border-red-400/50 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-red-500 transition-colors hover:bg-red-50">
                Simulate Failure
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
